/**
 * GA4 Event Tracking Functions
 * High-level functions for tracking specific events
 */
import { track, addBaseMetadata, getPageContext } from './track'
import {
  GA4Item,
  PageContext,
  SignUpEvent,
  LoginEvent,
  ViewItemEvent,
  AddToCartEvent,
  ViewCartEvent,
  SearchEvent,
  OrderStatusViewedEvent,
} from './types'
/**
 * Track page view
 */
export function trackPageView(pageType: PageContext['page_type']) {
  const context = getPageContext()
  track('page_view', {
    page_type: pageType,
    ...context,
  })
}
/**
 * Track sign up
 */
export function trackSignUp(
  method: 'email' | 'phone' | 'social',
  customerId?: string,
) {
  track<SignUpEvent>(
    'sign_up',
    addBaseMetadata({
      method,
      customer_id_sha256: customerId,
    }),
  )
}
/**
 * Track login
 */
export function trackLogin(
  method: 'email' | 'phone' | 'social',
  customerId?: string,
) {
  track<LoginEvent>(
    'login',
    addBaseMetadata({
      method,
      customer_id_sha256: customerId,
    }),
  )
}
/**
 * Track view item
 */
export function trackViewItem(items: GA4Item[]) {
  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  track<ViewItemEvent>(
    'view_item',
    addBaseMetadata({
      currency: 'SAR',
      value,
      items,
    }),
  )
}
/**
 * Track add to cart
 */
export function trackAddToCart(items: GA4Item[], cartId?: string) {
  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  track<AddToCartEvent>(
    'add_to_cart',
    addBaseMetadata({
      currency: 'SAR',
      value,
      cart_id: cartId,
      items,
    }),
  )
}
/**
 * Track view cart
 */
export function trackViewCart(items: GA4Item[]) {
  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  track<ViewCartEvent>(
    'view_cart',
    addBaseMetadata({
      currency: 'SAR',
      value,
      items,
    }),
  )
}

export function trackSearch(
  searchTerm: string,
  source: SearchEvent['search_source'],
  resultsCount?: number,
) {
  track<SearchEvent>('search', addBaseMetadata({
    search_term: searchTerm,
    search_source: source,
    results_count: resultsCount,
  }))
}

export function trackOrderStatusViewed(orderId: string, status: string, mode: 'now' | 'standard') {
  track<OrderStatusViewedEvent>('order_status_viewed', addBaseMetadata({
    order_id: orderId,
    order_status: status,
    order_mode: mode,
  }))
}
/**
 * Track begin checkout
 * NOTE: Requires backend integration to get cart_id
 */
export function trackBeginCheckout(items: GA4Item[], coupon?: string) {
  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  track(
    'begin_checkout',
    addBaseMetadata({
      currency: 'SAR',
      value,
      coupon,
      items,
    }),
  )
}
/**
 * Track add payment info
 * NOTE: Requires backend integration
 */
export function trackAddPaymentInfo(
  items: GA4Item[],
  paymentType:
    | 'Apple Pay'
    | 'Card'
    | 'STC Pay'
    | 'Tabby'
    | 'Tamara'
    | 'COD'
    | 'Mada',
) {
  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  track(
    'add_payment_info',
    addBaseMetadata({
      currency: 'SAR',
      value,
      payment_type: paymentType,
    }),
  )
}
/**
 * Track purchase
 * NOTE: Requires backend integration to get transaction_id
 */
export function trackPurchase(
  transactionId: string,
  items: GA4Item[],
  options?: {
    tax?: number
    shipping?: number
    discount?: number
    coupon?: string
  },
) {
  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  track(
    'purchase',
    addBaseMetadata({
      transaction_id: transactionId,
      affiliation: 'Dawaak',
      currency: 'SAR',
      value,
      ...options,
      items,
    }),
  )
}
/**
 * Track prescription cash order
 * NOTE: Requires backend integration
 */
export function trackPrescriptionCashOrder(
  transactionId: string,
  value: number,
  deliveryMethod: 'pickup' | 'delivery',
) {
  track(
    'prescription_cash_order',
    addBaseMetadata({
      transaction_id: transactionId,
      currency: 'SAR',
      value,
      prescription_type: 'cash',
      delivery_method: deliveryMethod,
    }),
  )
}
/**
 * Track prescription insurance order
 * NOTE: Requires backend integration
 */
export function trackPrescriptionInsuranceOrder(
  transactionId: string,
  value: number,
  provider?: string,
  deliveryMethod?: 'pickup' | 'delivery',
) {
  track(
    'prescription_insurance_order',
    addBaseMetadata({
      transaction_id: transactionId,
      currency: 'SAR',
      value,
      prescription_type: 'insurance',
      provider,
      delivery_method: deliveryMethod,
    }),
  )
}
/**
 * Track prescription wasfaty order
 * NOTE: Requires backend integration
 */
export function trackPrescriptionWasfatyOrder(
  transactionId: string,
  value: number,
  deliveryMethod?: 'pickup' | 'delivery',
) {
  track(
    'prescription_wasfaty_order',
    addBaseMetadata({
      transaction_id: transactionId,
      currency: 'SAR',
      value,
      prescription_type: 'wasfaty',
      delivery_method: deliveryMethod,
    }),
  )
}

export function trackPrescriptionExternalOrder(
  type: 'outpatient' | 'yusr',
  transactionId: string,
  deliveryMethod: 'pickup' | 'delivery',
) {
  track('prescription_external_order', addBaseMetadata({
    transaction_id: transactionId,
    prescription_type: type,
    delivery_method: deliveryMethod,
  }))
}
/**
 * Track prescription page view
 */
export function trackPrescriptionPageView(
  flow: 'cash' | 'insurance' | 'wasfaty' | 'outpatient' | 'yusr',
  step: 'start' | 'upload' | 'review' | 'approved' | 'rejected' | 'complete',
) {
  const context = getPageContext()
  track('prescription_page_view', {
    ...context,
    flow,
    step,
  })
}
